<template>
    <Head title="Work IQ" />
    <div class="min-h-full bg-valence-grey-50">
        <div class="mx-auto flex w-full max-w-7xl flex-col gap-7 px-6 py-8 md:px-10 lg:px-12">
            <div class="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                <BasePageTitle
                    title="Work IQ"
                    subtitle="Run the current Work IQ pillar prompts and preview the coaching insights prepared from your Microsoft 365 context. These signals may be incomplete."
                    class="min-w-0 flex-1" />
                <BaseLink v-if="is_staff" :href="workiq_playground_url" class="base-btn-secondary shrink-0 self-start sm:self-auto">
                    <i class="bi bi-sliders mr-2" aria-hidden="true" />
                    Open playground
                </BaseLink>
            </div>

            <div class="grid gap-7 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start">
                <main class="min-w-0">
                    <div class="mb-4 flex flex-wrap items-center justify-between gap-3">
                        <div>
                            <h2 class="title-section text-valence-grey-900">Insights</h2>
                            <p v-if="snapshots.length" class="body-small mt-1 text-valence-grey-750">
                                {{ snapshots.length }} current {{ snapshots.length === 1 ? "snapshot" : "snapshots" }} · {{ insights.length }}
                                {{ insights.length === 1 ? "insight" : "insights" }}
                            </p>
                        </div>
                        <BaseChip v-if="snapshots.length" size="xs">POC preview</BaseChip>
                    </div>

                    <BaseCard v-if="!insights.length" padding="lg" radius="xl" class="flex flex-col items-start gap-4">
                        <div class="flex h-11 w-11 items-center justify-center rounded-xl bg-valence-primary-100 text-valence-primary-500">
                            <i class="bi bi-stars text-xl" aria-hidden="true" />
                        </div>
                        <div>
                            <h3 class="title-body text-valence-grey-900">No current Work IQ insights yet</h3>
                            <p class="body mt-1 max-w-2xl text-valence-grey-750">
                                Run one of the pillar pipelines on this page. Its bounded snapshot and coaching cards will appear here without changing the existing My Work page.
                            </p>
                        </div>
                    </BaseCard>

                    <div v-else class="flex flex-col gap-5">
                        <BaseCard v-for="insight in insights" :key="insight.id" as="article" padding="none" radius="xl" class="overflow-hidden">
                            <div class="flex gap-5 p-5 sm:p-6">
                                <img :src="InsightGradientBar" alt="" class="w-2.5 shrink-0 self-stretch rounded-full object-cover" />
                                <div class="min-w-0 flex-1">
                                    <div class="mb-3 flex flex-wrap items-center gap-2">
                                        <span class="h-2 w-2 rounded-full bg-valence-primary-500" aria-hidden="true" />
                                        <span class="body-small-bold text-valence-grey-750">{{ categoryLabel(insight.category) }}</span>
                                        <BaseChip size="xs">{{ pillarLabel(insight.snapshot.pillar) }}</BaseChip>
                                        <StatusChip v-if="insight.freshness === 'recent'" state="neutral" size="xs">Recent</StatusChip>
                                        <BaseChip v-if="insight.confidence" size="xs">{{ insight.confidence }} confidence</BaseChip>
                                    </div>

                                    <h3 class="display-small-serif text-valence-grey-900">{{ insight.title }}</h3>
                                    <div class="my-4 border-t border-valence-grey-200" role="separator" />

                                    <MarkdownText :text="insight.body" class="body prose prose-slate max-w-none text-valence-grey-850 prose-p:my-2 prose-ul:my-2 prose-li:my-0" />

                                    <p v-if="insight.coaching_question" class="body-semibold mt-4 text-valence-primary-500">
                                        {{ insight.coaching_question }}
                                    </p>

                                    <details
                                        v-if="insight.evidence.length || insight.unknowns.length || insight.sources.length"
                                        class="mt-4 rounded-xl bg-valence-grey-50 px-4 py-3">
                                        <summary class="body-small-bold cursor-pointer text-valence-grey-750">Why Work IQ surfaced this</summary>
                                        <div class="mt-3 flex flex-col gap-3">
                                            <div v-if="insight.evidence.length">
                                                <p class="details font-semibold text-valence-grey-750">Signals</p>
                                                <ul class="body-small mt-1 list-disc space-y-1 pl-5 text-valence-grey-750">
                                                    <li v-for="evidence in insight.evidence" :key="evidence">{{ evidence }}</li>
                                                </ul>
                                            </div>
                                            <div v-if="insight.unknowns.length">
                                                <p class="details font-semibold text-valence-grey-750">What the data cannot tell us</p>
                                                <ul class="body-small mt-1 list-disc space-y-1 pl-5 text-valence-grey-750">
                                                    <li v-for="unknown in insight.unknowns" :key="unknown">{{ unknown }}</li>
                                                </ul>
                                            </div>
                                            <div v-if="insight.sources.length">
                                                <p class="details font-semibold text-valence-grey-750">Microsoft 365 sources</p>
                                                <ul class="mt-1 space-y-2">
                                                    <li v-for="source in insight.sources" :key="source.url">
                                                        <a
                                                            :href="source.url"
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            class="body-small group/source flex items-start gap-2 text-valence-primary-500 hover:underline">
                                                            <i class="bi bi-box-arrow-up-right mt-0.5 shrink-0" aria-hidden="true" />
                                                            <span>
                                                                <span class="font-semibold">{{ source.title }}</span>
                                                                <span class="block text-valence-grey-750">{{ source.source_type }} · {{ source.detail }}</span>
                                                            </span>
                                                        </a>
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                    </details>

                                    <div class="mt-5 flex flex-col gap-4 border-t border-valence-grey-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
                                        <div class="flex flex-wrap items-center gap-2">
                                            <span class="details text-valence-grey-750">Based on:</span>
                                            <SourceChip size="xs">Microsoft WorkIQ</SourceChip>
                                            <BaseChip size="xs">{{ insight.snapshot.prepared_period }}</BaseChip>
                                        </div>
                                        <BaseButton theme="primary" size="sm" :disabled="startingInsightId !== null" @click="diveDeeper(insight)">
                                            {{ startingInsightId === insight.id ? "Opening chat…" : insight.freshness === "recent" ? "Debrief" : "Dive Deeper" }}
                                        </BaseButton>
                                    </div>
                                </div>
                            </div>
                        </BaseCard>
                        <p v-if="chatError" role="alert" class="body-small text-red-700">{{ chatError }}</p>
                    </div>
                </main>

                <aside class="flex flex-col gap-5 lg:sticky lg:top-6 lg:max-h-[calc(100vh-3rem)] lg:overflow-y-auto lg:overscroll-contain lg:pr-2">
                    <section>
                        <h2 class="title-section mb-4 text-valence-grey-900">Context layers</h2>
                        <BaseCard padding="md" radius="xl">
                            <div class="flex items-center gap-3">
                                <div class="flex h-10 w-10 items-center justify-center rounded-xl bg-valence-primary-100 text-valence-primary-500">
                                    <i class="bi bi-diagram-3 text-lg" aria-hidden="true" />
                                </div>
                                <div class="min-w-0 flex-1">
                                    <p class="body-semibold text-valence-grey-900">Microsoft WorkIQ</p>
                                    <p class="details text-valence-grey-750">Email, calendar, docs and collaboration signals</p>
                                </div>
                                <StatusChip :state="workiq_connected ? 'active' : 'neutral'" size="xs">
                                    {{ workiq_connected ? "Connected" : "Not connected" }}
                                </StatusChip>
                            </div>
                            <p class="body-small mt-4 text-valence-grey-750">
                                Work IQ enriches these coaching topics with bounded factual context from your Microsoft 365 workflows.
                            </p>
                        </BaseCard>
                    </section>

                    <section v-if="calendarSnapshot">
                        <div class="mb-4 flex items-end justify-between gap-3">
                            <div>
                                <h2 class="title-section text-valence-grey-900">Calendar</h2>
                                <p class="details mt-1 text-valence-grey-750">Upcoming events retrieved through Work IQ</p>
                            </div>
                            <BaseChip size="xs">{{ calendarSnapshot.prepared_period }}</BaseChip>
                        </div>
                        <BaseCard padding="md" :radius="tonalThemeEnabled ? undefined : 'xl'">
                            <div v-if="calendarEventGroups.length" class="max-h-107.5 overflow-y-auto pr-1" :class="tonalThemeEnabled ? 'flex flex-col gap-4.5' : 'space-y-4'">
                                <div v-for="group in calendarEventGroups" :key="group.date" :class="{ 'space-y-2.5': !tonalThemeEnabled }">
                                    <p
                                        class="sticky top-0 z-10"
                                        :class="
                                            tonalThemeEnabled
                                                ? 'scaffold-details-small bg-surface-raised px-2.5 pb-2 text-ink-muted'
                                                : 'body-small bg-white py-1 font-medium text-valence-grey-700'
                                        ">
                                        {{ formatCalendarDay(group.date) }}&nbsp; {{ formatCalendarDate(group.date) }}
                                    </p>
                                    <div v-for="(event, eventIndex) in group.events" :key="`${event.start_time}-${event.title}`" :class="{ 'space-y-1.5': !tonalThemeEnabled }">
                                        <CalEvent
                                            :title="event.title"
                                            :start-time="event.start"
                                            :end-time="event.end"
                                            :attendees="event.displayAttendees"
                                            :actions-always-visible="tonalThemeEnabled"
                                            :divider="tonalThemeEnabled && eventIndex < group.events.length - 1"
                                            :calendar-color="event.event_type === 'one_on_one' ? '#7B61FF' : '#9596FF'"
                                            :is-past="isPastCalendarEvent(event)">
                                            <template #actions>
                                                <BaseButton
                                                    theme="primary"
                                                    size="sm"
                                                    :class="{ 'shrink-0 whitespace-nowrap px-3': !tonalThemeEnabled }"
                                                    :aria-label="`${calendarChatAction(event)} ${event.title} with Nadia`"
                                                    :disabled="startingCalendarEventId !== null || startingInsightId !== null"
                                                    @click.stop="startCalendarChat(event)">
                                                    {{ startingCalendarEventId === calendarEventKey(event) ? "Opening…" : calendarChatAction(event) }}
                                                </BaseButton>
                                            </template>
                                        </CalEvent>
                                        <div
                                            v-if="event.preparation_signal || event.change_type || event.is_recurring || event.sources.length"
                                            class="ml-3 flex flex-wrap gap-1.5 pl-2.5">
                                            <BaseChip v-if="event.is_recurring" size="xs">Recurring</BaseChip>
                                            <StatusChip v-if="event.change_type" state="pending" size="xs">{{ changeLabel(event.change_type) }}</StatusChip>
                                            <p v-if="event.preparation_signal" class="details text-valence-grey-750">
                                                <i class="bi bi-journal-check mr-1" aria-hidden="true" />{{ event.preparation_signal }}
                                            </p>
                                            <a
                                                v-for="source in event.sources"
                                                :key="source.url"
                                                :href="source.url"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                class="details inline-flex items-center gap-1 text-valence-primary-500 hover:underline">
                                                <i class="bi bi-box-arrow-up-right" aria-hidden="true" />{{ source.title }}
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div v-else class="flex gap-3 text-valence-grey-750">
                                <i class="bi bi-calendar2-check mt-0.5" aria-hidden="true" />
                                <div>
                                    <p class="body-small">Work IQ did not return sufficiently specific upcoming event times for this calendar view.</p>
                                    <details class="mt-2">
                                        <summary class="details cursor-pointer font-semibold">View the calendar source summary</summary>
                                        <MarkdownText :text="calendarSnapshot.summary" class="body-small mt-2 max-h-48 overflow-y-auto text-valence-grey-750" />
                                    </details>
                                </div>
                            </div>
                            <p class="details mt-4 border-t border-valence-grey-200 pt-3 text-valence-grey-750">
                                Read-only Work IQ snapshot · Updated {{ formatDate(calendarSnapshot.updated_at) }}
                            </p>
                            <p v-if="calendarChatError" role="alert" class="details mt-2 text-red-700">{{ calendarChatError }}</p>
                        </BaseCard>
                    </section>

                    <section>
                        <div class="mb-4 flex items-end justify-between gap-3">
                            <div>
                                <h2 class="title-section text-valence-grey-900">Pillar pipelines</h2>
                                <p class="details mt-1 text-valence-grey-750">Committed prompts and default variables</p>
                            </div>
                        </div>
                        <div class="flex flex-col gap-3">
                            <BaseCard v-for="pillar in availablePillars" :key="pillar.key" padding="sm" radius="xl">
                                <div class="flex items-start justify-between gap-3">
                                    <div class="min-w-0">
                                        <p class="body-small-bold text-valence-grey-900">{{ pillar.label }}</p>
                                        <p class="details mt-0.5 text-valence-grey-750">{{ pipelineDescription(pillar.key) }}</p>
                                        <p v-if="latestSnapshotForPillar(pillar.key)" class="details mt-1 text-valence-grey-750">
                                            Last run {{ formatDate(latestSnapshotForPillar(pillar.key)?.updated_at ?? null) }}
                                        </p>
                                    </div>
                                    <StatusChip :state="pipelineChipState(pillar.key)" size="xs">{{ pipelineStatusLabel(pillar.key) }}</StatusChip>
                                </div>

                                <div v-if="pillar.variables.length" class="mt-3 flex flex-col gap-2">
                                    <label
                                        v-for="variable in pillar.variables"
                                        :key="variable.name"
                                        :for="`workiq-insights-${pillar.key}-${variable.name}`"
                                        class="flex flex-col gap-1">
                                        <span class="details font-semibold text-valence-grey-750">{{ variable.label }}</span>
                                        <textarea
                                            v-if="variable.multiline"
                                            :id="`workiq-insights-${pillar.key}-${variable.name}`"
                                            v-model="pipelineVariables[pillar.key][variable.name]"
                                            rows="2"
                                            class="body-small w-full rounded-lg border border-valence-grey-200 bg-white px-3 py-2 focus:border-valence-primary-500 focus:outline-none" />
                                        <input
                                            v-else
                                            :id="`workiq-insights-${pillar.key}-${variable.name}`"
                                            v-model="pipelineVariables[pillar.key][variable.name]"
                                            type="text"
                                            class="body-small w-full rounded-lg border border-valence-grey-200 bg-white px-3 py-2 focus:border-valence-primary-500 focus:outline-none" />
                                    </label>
                                </div>

                                <BaseButton
                                    theme="secondary"
                                    size="sm"
                                    class="mt-3 w-full"
                                    :data-testid="`workiq-run-pipeline-${pillar.key}`"
                                    :disabled="!workiq_connected || activePillarKey !== null || hasMissingVariables(pillar)"
                                    @click="runPipeline(pillar)">
                                    {{ activePillarKey === pillar.key ? pipelineStatusLabel(pillar.key) : "Run pipeline" }}
                                </BaseButton>
                                <dl v-if="hasPipelineTiming(pillar.key)" class="mt-3 grid grid-cols-3 gap-2 rounded-lg bg-valence-grey-50 px-3 py-2 text-center">
                                    <div>
                                        <dt class="details text-valence-grey-750">Work IQ</dt>
                                        <dd class="body-small-bold tabular-nums text-valence-grey-900">{{ formatDuration(pipelineStates[pillar.key].askElapsedMs) }}</dd>
                                    </div>
                                    <div>
                                        <dt class="details text-valence-grey-750">Distill</dt>
                                        <dd class="body-small-bold tabular-nums text-valence-grey-900">{{ formatDuration(pipelineStates[pillar.key].distillElapsedMs) }}</dd>
                                    </div>
                                    <div>
                                        <dt class="details text-valence-grey-750">Total</dt>
                                        <dd class="body-small-bold tabular-nums text-valence-grey-900">{{ formatDuration(pipelineStates[pillar.key].totalElapsedMs) }}</dd>
                                    </div>
                                </dl>
                                <p v-if="hasMissingVariables(pillar)" class="details mt-2 text-valence-grey-750">Complete the inputs above to run this prompt.</p>
                                <p v-if="pipelineStates[pillar.key]?.error" role="alert" class="details mt-2 text-red-700">
                                    {{ pipelineStates[pillar.key].error }}
                                </p>
                            </BaseCard>
                        </div>
                    </section>

                    <section>
                        <h2 class="title-section mb-4 text-valence-grey-900">Working-pattern history</h2>
                        <BaseCard padding="md" radius="xl" class="flex flex-col gap-3">
                            <p class="body-small text-valence-grey-750">
                                The homepage working-patterns card needs {{ workiq_history_weeks_needed }} weekly periods before it can show a trend. This computes them from your
                                own past calendars — real data, no Work IQ ask and no LLM call.
                            </p>
                            <label for="workiq-backfill-weeks" class="flex flex-col gap-1">
                                <span class="details font-semibold text-valence-grey-750">Complete weeks to backfill</span>
                                <input
                                    id="workiq-backfill-weeks"
                                    v-model.number="backfillWeeks"
                                    type="number"
                                    min="1"
                                    :max="workiq_max_backfill_weeks"
                                    class="body-small w-full rounded-lg border border-valence-grey-200 bg-white px-3 py-2 focus:border-valence-primary-500 focus:outline-none" />
                            </label>
                            <BaseButton
                                theme="secondary"
                                size="sm"
                                data-testid="workiq-backfill-calendar"
                                :disabled="!workiq_connected || backfillRunning"
                                @click="backfillCalendarHistory">
                                {{ backfillRunning ? "Reading past calendars…" : "Backfill from my calendar" }}
                            </BaseButton>
                            <p class="details text-valence-grey-750">
                                Only complete weeks — this week's unbooked time is still a forecast. Open follow-ups can't be backfilled, because the Work IQ ask always answers
                                about the week ahead.
                            </p>
                            <p v-if="backfillSummary" class="details text-valence-grey-900">{{ backfillSummary }}</p>
                            <p v-if="backfillError" role="alert" class="details text-red-700">{{ backfillError }}</p>
                        </BaseCard>
                    </section>

                    <section>
                        <h2 class="title-section mb-4 text-valence-grey-900">Current snapshots</h2>
                        <BaseCard padding="md" radius="xl">
                            <dl v-if="snapshots.length" class="flex flex-col gap-4">
                                <div v-for="snapshot in snapshots" :key="snapshot.id" class="border-b border-valence-grey-200 pb-3 last:border-0 last:pb-0">
                                    <dt class="body-small-bold text-valence-grey-900">{{ pillarLabel(snapshot.pillar) }}</dt>
                                    <dd class="details mt-1 text-valence-grey-750">{{ snapshot.prepared_period }} · {{ formatDate(snapshot.updated_at) }}</dd>
                                    <dd class="details text-valence-grey-750">Available until {{ formatDate(snapshot.expires_at) }}</dd>
                                </div>
                            </dl>
                            <p v-else class="body-small text-valence-grey-750">No current snapshots are available.</p>
                        </BaseCard>
                    </section>

                    <BaseCard theme="accent" padding="md" radius="xl">
                        <div class="flex gap-3">
                            <i class="bi bi-info-circle text-valence-primary-500" aria-hidden="true" />
                            <p class="body-small text-valence-grey-750">
                                This is a temporary POC. Work IQ context is intentionally bounded and should not be treated as a complete account of your work.
                            </p>
                        </div>
                    </BaseCard>
                </aside>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import LeftSidebarLayout from "~vue/layouts/LeftSidebarLayout.vue";

export interface StoredWorkIqInsight {
    category: string;
    title: string;
    body: string;
    coaching_question: string | null;
    confidence: string | null;
    evidence: string[];
    unknowns: string[];
    sources?: WorkIqSource[];
    // "upcoming" while the item's own surface_until hasn't passed; "recent" once it has — a
    // debrief opportunity, not stale data. See workiq.snapshots.classify_distilled_items.
    freshness?: "upcoming" | "recent";
}

export interface WorkIqInsight extends StoredWorkIqInsight {
    id: string;
    snapshot: WorkIqSnapshot;
    sources: WorkIqSource[];
    source_kind: "insight" | "snapshot";
    source_index: number | null;
}

export interface WorkIqSource {
    url: string;
    source_type: string;
    title: string;
    detail: string;
}

export interface WorkIqCitation {
    url: string;
    title: string;
}

export interface WorkIqSnapshot {
    id: string;
    pillar: string;
    scope_key: string;
    prepared_period: string;
    summary: string;
    distilled: StoredWorkIqInsight[] | null;
    citations: WorkIqCitation[];
    calendar_events: StoredWorkIqCalendarEvent[];
    expires_at: string | null;
    updated_at: string;
}

export interface StoredWorkIqCalendarEvent {
    title: string;
    start_time: string;
    end_time: string;
    organizer: string | null;
    attendees: string[];
    all_day: boolean;
    is_recurring: boolean;
    event_type: "one_on_one" | "group" | "external" | "decision_review" | "focus" | "other";
    preparation_signal: string | null;
    change_type: "added" | "moved" | "shortened" | "extended" | "cancelled" | "attendees_changed" | null;
    sources?: WorkIqSource[];
}

export interface PillarVariableSpec {
    name: string;
    label: string;
    multiline: boolean;
    default: string;
}

export interface PillarSpec {
    key: string;
    label: string;
    available: boolean;
    distillable: boolean;
    variables: PillarVariableSpec[];
}

export default { layout: LeftSidebarLayout };
</script>

<script setup lang="ts">
import { Head, router } from "@inertiajs/vue3";
import InsightGradientBar from "~img/insight-gradient-bar.png";
import { useFetch } from "~vue/api";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseChip from "~vue/components/BaseChip.vue";
import BasePageTitle from "~vue/components/BasePageTitle.vue";
import CalEvent from "~vue/components/CalendarIntegration/CalEvent.vue";
import SourceChip from "~vue/components/SourceChip.vue";
import StatusChip from "~vue/components/StatusChip.vue";
import MarkdownText from "~vue/MarkdownText.vue";
import BaseLink from "~vue/router/BaseLink.vue";
import { logError } from "~vue/utils/logUtils";
import { tonalThemeEnabled } from "~vue/utils/theme";
import { DateTime } from "luxon";
import { computed, onUnmounted, reactive, ref } from "vue";

interface AskResponse {
    answer?: string;
    error?: string;
    snapshot?: WorkIqSnapshot | null;
}

interface DistillStartResponse {
    task_id?: string;
    error?: string;
}

interface DistillStatusResponse {
    ready?: boolean;
    state?: string;
    status?: string;
    reason?: string;
    error?: string;
    snapshot?: WorkIqSnapshot | null;
}

type PipelinePhase = "idle" | "asking" | "distilling" | "completed" | "failed";

interface PipelineState {
    phase: PipelinePhase;
    error: string;
    askElapsedMs: number;
    distillElapsedMs: number;
    totalElapsedMs: number;
}

interface DisplayCalendarEvent extends StoredWorkIqCalendarEvent {
    start: DateTime;
    end: DateTime;
    displayAttendees: { name: string; email: string }[];
    sources: WorkIqSource[];
    sourceIndex: number;
}

interface StartChatResponse {
    redirect_url?: string;
    error?: string;
}

interface StartChatPayload {
    chat_source: string;
    initial_message: string;
    workiq_snapshot_id?: string;
    workiq_source_kind?: "insight" | "calendar_event" | "snapshot";
    workiq_source_index?: number | null;
}

// TODO: Remove the legacy calendar branch once the tonal theme becomes the default.

const props = withDefaults(
    defineProps<{
        snapshots?: WorkIqSnapshot[];
        workiq_connected: boolean;
        workiq_pillars?: PillarSpec[];
        workiq_ask_pillar_url?: string;
        workiq_distill_pillar_url?: string;
        workiq_run_insights_status_url?: string;
        workiq_backfill_calendar_url?: string;
        workiq_max_backfill_weeks?: number;
        workiq_history_weeks_needed?: number;
        workiq_playground_url?: string;
        is_staff?: boolean;
    }>(),
    {
        snapshots: () => [],
        workiq_pillars: () => [],
        workiq_ask_pillar_url: "/workiq/api/ask-pillar/",
        workiq_distill_pillar_url: "/workiq/api/distill-pillar/",
        workiq_run_insights_status_url: "/workiq/api/run-insights/status/",
        workiq_backfill_calendar_url: "/workiq/api/snapshots/backfill-calendar/",
        workiq_max_backfill_weeks: 12,
        workiq_history_weeks_needed: 6,
        workiq_playground_url: "/admin/workiq/playground/",
        is_staff: false,
    },
);

const categoryLabels: Record<string, string> = {
    meeting_prep: "Meeting prep",
    calendar_change: "Calendar change",
    open_loop: "Open loop",
    relationship: "Relationship",
    goal_progress: "Goal progress",
    workload: "Workload",
    overview: "My Work overview",
};

const pillarLabels: Record<string, string> = Object.fromEntries(props.workiq_pillars.map((pillar) => [pillar.key, pillar.label]));
const snapshots = ref<WorkIqSnapshot[]>([...props.snapshots]);

const insights = computed<WorkIqInsight[]>(() => {
    return snapshots.value.flatMap((snapshot) => {
        if (snapshot.distilled?.length) {
            return snapshot.distilled.map<WorkIqInsight>((insight, index) => ({
                ...insight,
                id: `${snapshot.id}-${index}`,
                snapshot,
                coaching_question: insight.coaching_question ?? null,
                confidence: insight.confidence ?? null,
                evidence: insight.evidence ?? [],
                unknowns: insight.unknowns ?? [],
                sources: insight.sources ?? [],
                source_kind: "insight" as const,
                source_index: index,
            }));
        }
        return [
            {
                id: snapshot.id,
                snapshot,
                category: "overview",
                title: `${pillarLabel(snapshot.pillar)} context`,
                body: snapshot.summary,
                coaching_question: "Which part of this picture would be most useful to work through with Nadia?",
                confidence: null,
                evidence: [],
                unknowns: [],
                sources: snapshot.citations.map((citation) => ({
                    ...citation,
                    source_type: "Microsoft 365",
                    detail: "Source returned by Work IQ for this factual snapshot.",
                })),
                source_kind: "snapshot" as const,
                source_index: null,
            } satisfies WorkIqInsight,
        ];
    });
});

const availablePillars = computed(() => props.workiq_pillars.filter((pillar) => pillar.available));
const pipelineVariables = reactive<Record<string, Record<string, string>>>(
    Object.fromEntries(props.workiq_pillars.map((pillar) => [pillar.key, Object.fromEntries(pillar.variables.map((variable) => [variable.name, variable.default]))])),
);
const pipelineStates = reactive<Record<string, PipelineState>>(
    Object.fromEntries(
        props.workiq_pillars.map((pillar) => [pillar.key, { phase: "idle", error: "", askElapsedMs: 0, distillElapsedMs: 0, totalElapsedMs: 0 } satisfies PipelineState]),
    ),
);
const activePillarKey = ref<string | null>(null);
let unmounted = false;
let pipelineTimer: number | null = null;

const calendarSnapshot = computed(() => snapshots.value.find((snapshot) => snapshot.pillar === "calendar"));
const calendarEventGroups = computed(() => {
    const groups = new Map<string, DisplayCalendarEvent[]>();
    for (const [sourceIndex, event] of (calendarSnapshot.value?.calendar_events ?? []).entries()) {
        const start = DateTime.fromISO(event.start_time, { setZone: true });
        const end = DateTime.fromISO(event.end_time, { setZone: true });
        if (!start.isValid || !end.isValid) {
            continue;
        }
        const date = start.toISODate();
        if (!date) {
            continue;
        }
        const displayEvent = {
            ...event,
            start,
            end,
            displayAttendees: event.attendees.map((name) => ({ name, email: "" })),
            sources: event.sources ?? [],
            sourceIndex,
        };
        const events = groups.get(date) ?? [];
        events.push(displayEvent);
        groups.set(date, events);
    }
    return [...groups.entries()]
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([date, events]) => ({ date, events: events.sort((left, right) => left.start.toMillis() - right.start.toMillis()) }));
});

const pipelineDescriptions: Record<string, string> = {
    my_work: "Priorities, open loops, meetings and workload",
    calendar: "Upcoming meetings, preparation signals and observable calendar changes",
    collaboration_profiles: "Observable collaboration with a named colleague",
    journeys: "Work signals connected to active coaching goals",
    performance_management: "Bounded evidence for a review period",
    collaboration_hub: "Patterns across a named team roster",
};

function pillarLabel(pillar: string): string {
    return pillarLabels[pillar] ?? pillar.replaceAll("_", " ");
}

function pipelineDescription(pillar: string): string {
    return pipelineDescriptions[pillar] ?? "Run the committed Work IQ prompt and distill its current insights";
}

function pipelineStatusLabel(pillar: string): string {
    const labels: Record<PipelinePhase, string> = {
        idle: "Ready",
        asking: "Running Work IQ…",
        distilling: "Distilling…",
        completed: "Complete",
        failed: "Failed",
    };
    return labels[pipelineStates[pillar]?.phase ?? "idle"];
}

function pipelineChipState(pillar: string) {
    const phase = pipelineStates[pillar]?.phase ?? "idle";
    if (phase === "completed") {
        return "done";
    }
    if (phase === "failed") {
        return "neutral";
    }
    if (phase === "asking" || phase === "distilling") {
        return "active";
    }
    return "neutral";
}

function hasMissingVariables(pillar: PillarSpec): boolean {
    return pillar.variables.some((variable) => !pipelineVariables[pillar.key]?.[variable.name]?.trim());
}

function hasPipelineTiming(pillar: string): boolean {
    const state = pipelineStates[pillar];
    return state.phase !== "idle" || state.totalElapsedMs > 0;
}

function formatDuration(milliseconds: number): string {
    return `${(milliseconds / 1000).toFixed(1)}s`;
}

function formatCalendarDay(value: string): string {
    const date = DateTime.fromISO(value);
    if (date.hasSame(DateTime.now(), "day")) {
        return "Today";
    }
    if (date.hasSame(DateTime.now().plus({ days: 1 }), "day")) {
        return "Tomorrow";
    }
    return date.toFormat("cccc");
}

function formatCalendarDate(value: string): string {
    return DateTime.fromISO(value).toFormat("MMM d");
}

function changeLabel(value: NonNullable<StoredWorkIqCalendarEvent["change_type"]>): string {
    return value === "attendees_changed" ? "Attendees changed" : value.charAt(0).toUpperCase() + value.slice(1);
}

function calendarEventKey(event: DisplayCalendarEvent): string {
    return `${event.start_time}-${event.end_time}-${event.title}`;
}

function isPastCalendarEvent(event: DisplayCalendarEvent): boolean {
    return event.end.toMillis() < DateTime.now().toMillis();
}

function calendarChatAction(event: DisplayCalendarEvent): "Prepare" | "Debrief" {
    return isPastCalendarEvent(event) ? "Debrief" : "Prepare";
}

function stopPipelineTimer() {
    if (pipelineTimer !== null) {
        globalThis.clearInterval(pipelineTimer);
        pipelineTimer = null;
    }
}

function latestSnapshotForPillar(pillar: string): WorkIqSnapshot | undefined {
    return snapshots.value.find((snapshot) => snapshot.pillar === pillar);
}

function upsertSnapshot(snapshot: WorkIqSnapshot) {
    const existingIndex = snapshots.value.findIndex((current) => current.id === snapshot.id);
    if (existingIndex >= 0) {
        snapshots.value.splice(existingIndex, 1, snapshot);
    } else {
        snapshots.value.unshift(snapshot);
    }
    snapshots.value.sort((left, right) => right.updated_at.localeCompare(left.updated_at));
}

async function pollDistill(taskId: string): Promise<DistillStatusResponse> {
    const deadline = Date.now() + 6 * 60 * 1000;
    const url = `${props.workiq_run_insights_status_url}?task_id=${encodeURIComponent(taskId)}`;
    while (!unmounted && Date.now() < deadline) {
        const { data, response } = await useFetch(url).get().json<DistillStatusResponse>();
        if (!response.value?.ok) {
            throw new Error(data.value?.error || "Status check failed.");
        }
        if (data.value?.ready) {
            return data.value;
        }
        await new Promise((resolve) => globalThis.setTimeout(resolve, 2500));
    }
    throw new Error(unmounted ? "Pipeline polling stopped." : "Timed out waiting for the distill. Is the Celery worker running?");
}

interface BackfillWeek {
    prepared_period: string;
    week_monday: string;
    status: "written" | "no_calendar_data";
}

interface BackfillResponse {
    weeks?: BackfillWeek[];
    written?: number;
    error?: string;
}

const backfillWeeks = ref(props.workiq_max_backfill_weeks);
const backfillRunning = ref(false);
const backfillError = ref("");
const backfillResult = ref<BackfillResponse | null>(null);

const backfillSummary = computed(() => {
    const result = backfillResult.value;
    if (!result?.weeks) {
        return "";
    }
    const written = result.written ?? 0;
    const empty = result.weeks.length - written;
    const emptyNote = empty > 0 ? `, ${empty} with no calendar data` : "";
    return `Wrote ${written} of ${result.weeks.length} weeks${emptyNote}.`;
});

async function backfillCalendarHistory() {
    if (backfillRunning.value) {
        return;
    }
    backfillRunning.value = true;
    backfillError.value = "";
    backfillResult.value = null;
    try {
        const { data, response } = await useFetch(props.workiq_backfill_calendar_url).post({ weeks: backfillWeeks.value }).json<BackfillResponse>();
        if (!response.value?.ok) {
            throw new Error(data.value?.error || "The backfill did not complete.");
        }
        backfillResult.value = data.value;
    } catch (error) {
        backfillError.value = error instanceof Error ? error.message : "The backfill failed.";
        logError(error);
    } finally {
        backfillRunning.value = false;
    }
}

async function runPipeline(pillar: PillarSpec) {
    if (activePillarKey.value || !props.workiq_connected || hasMissingVariables(pillar)) {
        return;
    }

    const state = pipelineStates[pillar.key];
    activePillarKey.value = pillar.key;
    state.phase = "asking";
    state.error = "";
    state.askElapsedMs = 0;
    state.distillElapsedMs = 0;
    state.totalElapsedMs = 0;
    const variables = { ...pipelineVariables[pillar.key] };
    const totalStartedAt = globalThis.performance.now();
    let phaseStartedAt = totalStartedAt;
    let distillStartedAt: number | null = null;
    stopPipelineTimer();
    pipelineTimer = globalThis.setInterval(() => {
        const elapsed = globalThis.performance.now();
        state.totalElapsedMs = elapsed - totalStartedAt;
        if (state.phase === "asking") {
            state.askElapsedMs = elapsed - phaseStartedAt;
        } else if (state.phase === "distilling") {
            state.distillElapsedMs = elapsed - phaseStartedAt;
        }
    }, 100);

    try {
        const { data: askData, response: askResponse } = await useFetch(props.workiq_ask_pillar_url).post({ pillar: pillar.key, variables }).json<AskResponse>();
        if (!askResponse.value?.ok || !askData.value?.answer) {
            throw new Error(askData.value?.error || "Work IQ did not return an answer.");
        }
        if (askData.value.snapshot) {
            upsertSnapshot(askData.value.snapshot);
        }
        state.askElapsedMs = globalThis.performance.now() - phaseStartedAt;
        if (!pillar.distillable) {
            state.phase = "completed";
            return;
        }

        state.phase = "distilling";
        phaseStartedAt = globalThis.performance.now();
        distillStartedAt = phaseStartedAt;
        const { data: startData, response: startResponse } = await useFetch(props.workiq_distill_pillar_url)
            .post({ pillar: pillar.key, answer: askData.value.answer, variables })
            .json<DistillStartResponse>();
        if (!startResponse.value?.ok || !startData.value?.task_id) {
            throw new Error(startData.value?.error || "Could not start the distill step.");
        }

        const result = await pollDistill(startData.value.task_id);
        if (result.status !== "completed" || !result.snapshot) {
            throw new Error(result.error || result.reason || "The distill step did not complete.");
        }
        upsertSnapshot(result.snapshot);
        state.distillElapsedMs = globalThis.performance.now() - phaseStartedAt;
        state.phase = "completed";
    } catch (error) {
        state.phase = "failed";
        state.error = error instanceof Error ? error.message : "Pipeline failed.";
        logError(error);
    } finally {
        state.totalElapsedMs = globalThis.performance.now() - totalStartedAt;
        if (distillStartedAt !== null) {
            state.distillElapsedMs = globalThis.performance.now() - distillStartedAt;
        } else if (state.phase === "failed") {
            state.askElapsedMs = state.totalElapsedMs;
        }
        stopPipelineTimer();
        activePillarKey.value = null;
    }
}

onUnmounted(() => {
    unmounted = true;
    stopPipelineTimer();
});

const startingInsightId = ref<string | null>(null);
const startingCalendarEventId = ref<string | null>(null);
const chatError = ref<string | null>(null);
const calendarChatError = ref<string | null>(null);
const startChatPayload = ref<StartChatPayload | null>(null);
const {
    data: startChatResponse,
    error: startChatRequestError,
    execute: executeStartChat,
} = useFetch("/coach/start/", { immediate: false }).post(startChatPayload).json<StartChatResponse>();

function categoryLabel(category: string): string {
    return categoryLabels[category] ?? category.replaceAll("_", " ");
}

function formatDate(value: string | null): string {
    if (!value) {
        return "—";
    }
    return DateTime.fromISO(value).toLocaleString(DateTime.DATETIME_MED);
}

async function diveDeeper(insight: WorkIqInsight) {
    if (startingInsightId.value || startingCalendarEventId.value) {
        return;
    }
    startingInsightId.value = insight.id;
    chatError.value = null;
    startChatPayload.value = {
        chat_source: "workiq_insights",
        workiq_snapshot_id: insight.snapshot.id,
        workiq_source_kind: insight.source_kind,
        workiq_source_index: insight.source_index,
        initial_message: [
            "I'd like coaching on this Work IQ insight.",
            `Topic: ${insight.title}`,
            `Context: ${insight.body}`,
            insight.coaching_question ? `Question to explore: ${insight.coaching_question}` : "",
            "Treat this as bounded context that may be incomplete. Help me understand the underlying tension and choose a useful next step.",
        ]
            .filter(Boolean)
            .join("\n\n"),
    };

    await executeStartChat();
    if (!startChatRequestError.value && startChatResponse.value?.redirect_url) {
        router.visit(startChatResponse.value.redirect_url);
        return;
    }

    chatError.value = startChatResponse.value?.error || "Could not start a chat. Please try again.";
    logError(chatError.value);
    startingInsightId.value = null;
}

async function startCalendarChat(event: DisplayCalendarEvent) {
    if (startingCalendarEventId.value || startingInsightId.value) {
        return;
    }

    const action = calendarChatAction(event);
    startingCalendarEventId.value = calendarEventKey(event);
    calendarChatError.value = null;
    startChatPayload.value = {
        chat_source: "workiq_insights",
        workiq_snapshot_id: calendarSnapshot.value?.id,
        workiq_source_kind: "calendar_event",
        workiq_source_index: event.sourceIndex,
        initial_message: [
            `I'd like to ${action.toLowerCase()} for this meeting using my Work IQ calendar context.`,
            `Meeting: ${event.title}`,
            `When: ${event.start.toLocaleString(DateTime.DATETIME_FULL)} to ${event.end.toLocaleString(DateTime.TIME_SIMPLE)}`,
            event.organizer ? `Organizer: ${event.organizer}` : "",
            event.attendees.length ? `Attendees: ${event.attendees.join(", ")}` : "",
            event.preparation_signal ? `Observable preparation signal: ${event.preparation_signal}` : "",
            event.change_type ? `Observable calendar change: ${changeLabel(event.change_type)}` : "",
            action === "Prepare"
                ? "Help me clarify the outcome I want, what I should prepare, and how I want to show up."
                : "Help me reflect on what happened, what I learned, and any useful follow-up.",
            "Treat this as bounded Microsoft 365 context that may be incomplete. Do not assume details that are not stated above.",
        ]
            .filter(Boolean)
            .join("\n\n"),
    };

    await executeStartChat();
    if (!startChatRequestError.value && startChatResponse.value?.redirect_url) {
        router.visit(startChatResponse.value.redirect_url);
        return;
    }

    calendarChatError.value = startChatResponse.value?.error || "Could not start a chat. Please try again.";
    logError(calendarChatError.value);
    startingCalendarEventId.value = null;
}
</script>
